import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type {
  Asset,
  VideoMedia,
  VisualMatch,
} from '@frameio/graphql-client/generated';
import { getThumbMedia } from '@frameio/shared/finder/utils';
import { EMPTY_OBJECT, timeToStandard } from '@frameio/utils';
import { convertMillisecondsToSeconds } from '@frameio/utils/formatters';
import {
  Box,
  type IMediaThumbnail,
  MediaThumbnail,
  Text,
  theme,
} from '@frameio/vapor';
import { useSetAssetClipMatch } from '../../../../../../hooks';
import { ImageWrapper } from '../../../../../../shared/components';
import { useQuickSearchModal } from '../../../../../../shared/contexts';
import type { QuickSearchSupportedAssetMediaV2Fragment } from '../../../../../../shared/gql/generated/QuickSearchSupportedAssetMediaV2';
import { useQuickSearchSupportedAssetMediaV2Fragment } from '../../../../../../shared/gql/generated/QuickSearchSupportedAssetMediaV2';
import type { OnMatchClickHandler } from '../../../../../../shared/types';
import {
  getAssetResultItemLink,
  getPaddedScrubRange,
  prepareAssetClipMatch,
} from '../../../../../../shared/utils';
import { MatchCard, MatchCardsContainer } from './shared';

const ASSET_TYPENAME = 'VideoAsset';
const HEIGHT = 57.68;
const WIDTH = 103;

const formatTimeRange = ({
  startsAt,
  endsAt,
}: {
  startsAt: string;
  endsAt: string;
}) => `${startsAt} – ${endsAt}`;

type ClipMatchItemProps = {
  assetId: string;
  visualMatch: VisualMatch;
  onMatchClick?: OnMatchClickHandler;
  onClick: (
    assetData: QuickSearchSupportedAssetMediaV2Fragment,
    startsAtMicroseconds: number,
    durationSeconds: number
  ) => void;
};

const ClipMatchItem = ({
  visualMatch,
  assetId,
  onMatchClick,
  onClick,
}: ClipMatchItemProps) => {
  const { data: assetData } =
    useQuickSearchSupportedAssetMediaV2Fragment(assetId);

  const media: IMediaThumbnail =
    getThumbMedia(assetData as QuickSearchSupportedAssetMediaV2Fragment) ??
    EMPTY_OBJECT;

  const videoDuration =
    'media' in assetData
      ? (assetData.media as VideoMedia)?.duration
      : undefined;
  const frameCount = media?.thumbs?.thumbScrubFrameCount;

  const setAssetClipMatch = useSetAssetClipMatch();

  const handleOnClick = useCallback(
    (activeClipIndex: number) => {
      onMatchClick?.(visualMatch.__typename ?? 'VisualMatch');

      const { currentTime, duration } = prepareAssetClipMatch({
        assetId,
        visualMatch,
        activeClipIndex,
        setAssetClipMatch,
      });

      onClick(
        assetData as QuickSearchSupportedAssetMediaV2Fragment,
        currentTime,
        duration
      );
    },
    [assetId, visualMatch, onMatchClick, setAssetClipMatch, onClick, assetData]
  );

  if (!visualMatch.clips?.length) return null;

  return (
    <>
      {visualMatch.clips.map((clip) => {
        const startsAtInSeconds = convertMillisecondsToSeconds(
          clip?.startTime ?? 0
        );
        const endsAtInSeconds = convertMillisecondsToSeconds(
          clip?.endTime ?? 0
        );

        const { paddedStartsAt, paddedEndsAt } = getPaddedScrubRange(
          startsAtInSeconds,
          endsAtInSeconds,
          videoDuration ?? undefined,
          frameCount
        );

        return (
          <MatchCard key={`clip-match-${clip?.startTime}`}>
            <Box
              alignItems="center"
              display="flex"
              gap={12}
              justifyContent="space-between"
              onClick={() => handleOnClick(clip?.index ?? 0)}
            >
              <Box
                display="flex"
                flexDirection="column"
                gap={4}
                overflow="hidden"
              >
                <Text color="body" truncate={true} variant="secondary">
                  {formatTimeRange({
                    startsAt: timeToStandard(startsAtInSeconds),
                    endsAt: timeToStandard(endsAtInSeconds),
                  })}
                </Text>
              </Box>
              <ImageWrapper borderRadius={4}>
                <MediaThumbnail
                  assetTypename={ASSET_TYPENAME}
                  borderRadius={theme.radii[4]}
                  height={HEIGHT}
                  isImageFitPreferred={true}
                  isScrubEnabled={true}
                  media={media}
                  scrubBarWidth={2}
                  scrubRangeEnd={paddedEndsAt}
                  scrubRangeStart={paddedStartsAt}
                  videoDuration={videoDuration ?? undefined}
                  width={WIDTH}
                />
              </ImageWrapper>
            </Box>
          </MatchCard>
        );
      })}
    </>
  );
};

type ClipMatchProps = {
  visualMatches: VisualMatch[];
  assetId: string;
  onMatchClick?: OnMatchClickHandler;
};

export const ClipMatch = ({
  visualMatches,
  assetId,
  onMatchClick,
}: ClipMatchProps) => {
  const router = useRouter();
  const { closeModal } = useQuickSearchModal();

  const handleClipMatchNavigation = useCallback(
    (
      assetData: QuickSearchSupportedAssetMediaV2Fragment,
      startsAtMicroseconds: number,
      durationSeconds: number
    ) => {
      const href = getAssetResultItemLink(assetData as Asset, {
        isRecentlyDeleted: false,
        currentTime: startsAtMicroseconds,
        duration: durationSeconds,
      });
      router.push(href);
      closeModal({ navigatingTo: href });
    },
    [router, closeModal]
  );

  if (!visualMatches?.length) return null;

  return (
    <MatchCardsContainer>
      {visualMatches.map((visualMatch, index) => (
        <ClipMatchItem
          key={`clip-match-${index}`}
          assetId={assetId}
          onClick={handleClipMatchNavigation}
          onMatchClick={onMatchClick}
          visualMatch={visualMatch}
        />
      ))}
    </MatchCardsContainer>
  );
};
